/**
 * 客户门户 Store
 *
 * 使用场景：客户登录状态管理
 * 独立于内部员工 user store 和客户档案管理，互不影响
 * 与内部员工 user store 使用不同的 localStorage key 隔离
 *
 * @author system
 * @date 2026-08-11
 */

import {
    customerPortalLogin,
    customerPortalLogout,
    getCustomerPortalInfo
} from '@/api/customer-portal'

/**
 * 获取客户门户 Token
 */
function getCustomerPortalToken() {
    return localStorage.getItem('CUSTOMER_PORTAL_TOKEN')
}

/**
 * 设置客户门户 Token
 */
function setCustomerPortalToken(token) {
    localStorage.setItem('CUSTOMER_PORTAL_TOKEN', token)
}

/**
 * 删除客户门户 Token
 */
function removeCustomerPortalToken() {
    localStorage.removeItem('CUSTOMER_PORTAL_TOKEN')
}

const state = {
    /** 客户门户 Token */
    token: getCustomerPortalToken(),
    /** 客户账号信息 */
    account: null,
    /** 是否已登录 */
    isLoggedIn: false,
    /** 是否需要强制修改密码（首次登录初始密码） */
    needChangePwd: false
}

const mutations = {
    /**
     * 设置客户门户 Token
     */
    SET_TOKEN: (state, token) => {
        state.token = token
        if (token) {
            setCustomerPortalToken(token)
        } else {
            removeCustomerPortalToken()
        }
    },

    /**
     * 设置客户账号信息
     */
    SET_ACCOUNT: (state, account) => {
        state.account = account
        if (account) {
            localStorage.setItem('CUSTOMER_PORTAL_ACCOUNT', JSON.stringify(account))
        } else {
            localStorage.removeItem('CUSTOMER_PORTAL_ACCOUNT')
        }
    },

    /**
     * 设置登录状态
     */
    SET_LOGGED_IN: (state, flag) => {
        state.isLoggedIn = flag
    },

    /**
     * 设置是否需要强制修改密码
     */
    SET_NEED_CHANGE_PWD: (state, need) => {
        state.needChangePwd = need
        if (need) {
            localStorage.setItem('CUSTOMER_PORTAL_NEED_CHANGE_PWD', 'true')
        } else {
            localStorage.removeItem('CUSTOMER_PORTAL_NEED_CHANGE_PWD')
        }
    },

    /**
     * 退出登录（清除所有客户门户状态）
     */
    LOGOUT: (state) => {
        state.token = null
        state.account = null
        state.isLoggedIn = false
        state.needChangePwd = false
        removeCustomerPortalToken()
        localStorage.removeItem('CUSTOMER_PORTAL_ACCOUNT')
        localStorage.removeItem('CUSTOMER_PORTAL_NEED_CHANGE_PWD')
    }
}

const actions = {
    /**
     * 客户门户登录
     */
    login({ commit }, loginData) {
        return new Promise((resolve, reject) => {
            customerPortalLogin(loginData).then(res => {
                const data = res.data
                commit('SET_TOKEN', data.token)
                commit('SET_ACCOUNT', {
                    accountId: data.accountId,
                    customerId: data.customerId,
                    contactName: data.contactName,
                    accountType: data.accountType
                })
                commit('SET_LOGGED_IN', true)
                commit('SET_NEED_CHANGE_PWD', data.needChangePwd || false)
                resolve(data)
            }).catch(err => {
                reject(err)
            })
        })
    },

    /**
     * 客户门户退出登录
     */
    logout({ commit }) {
        return new Promise((resolve) => {
            const token = localStorage.getItem('CUSTOMER_PORTAL_TOKEN')
            if (token) {
                fetch('/api/customer/logout', {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }).catch(() => {})
            }
            commit('LOGOUT')
            resolve()
        })
    },

    /**
     * 获取客户门户信息（优先从缓存读取）
     */
    getInfo({ commit, state }) {
        return new Promise((resolve) => {
            // 已有账号信息，直接返回
            if (state.account) {
                resolve({
                    account: state.account,
                    needChangePwd: state.needChangePwd
                })
                return
            }

            // 从缓存恢复
            const cachedAccount = localStorage.getItem('CUSTOMER_PORTAL_ACCOUNT')
            const cachedNeedChangePwd = localStorage.getItem('CUSTOMER_PORTAL_NEED_CHANGE_PWD')

            if (cachedAccount) {
                try {
                    commit('SET_ACCOUNT', JSON.parse(cachedAccount))
                    commit('SET_LOGGED_IN', true)
                    if (cachedNeedChangePwd) {
                        commit('SET_NEED_CHANGE_PWD', cachedNeedChangePwd === 'true')
                    }
                    resolve({
                        account: JSON.parse(cachedAccount),
                        needChangePwd: cachedNeedChangePwd === 'true'
                    })
                    return
                } catch (e) {}
            }

            // 缓存中没有，从后端获取
            getCustomerPortalInfo().then(res => {
                const data = res.data
                commit('SET_ACCOUNT', {
                    accountId: data.accountId,
                    customerId: data.customerId,
                    contactName: data.contactName,
                    accountType: data.accountType
                })
                commit('SET_LOGGED_IN', true)
                resolve({
                    account: data,
                    needChangePwd: state.needChangePwd
                })
            }).catch(() => {
                resolve({
                    account: null,
                    needChangePwd: false
                })
            })
        })
    },

    /**
     * 标记密码已修改
     */
    markPasswordChanged({ commit }) {
        commit('SET_NEED_CHANGE_PWD', false)
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions
}